import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { type CreateTagInput, type TagAdminItem, createTagSchema } from '@news-cms/shared';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { slugifyForPreview } from '@/lib/slugify';
import { taxonomyErrorMessage, useCreateTag, useUpdateTag } from '@/lib/taxonomies';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing?: TagAdminItem;
}

export function TagEditDialog({ open, onOpenChange, editing }: Props) {
  const isEdit = Boolean(editing);
  const create = useCreateTag();
  const update = useUpdateTag();
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<CreateTagInput>({
    resolver: zodResolver(createTagSchema),
    defaultValues: { name: '', slug: '' },
  });

  useEffect(() => {
    if (!open) return;
    setSlugManuallyEdited(Boolean(editing?.slug));
    reset({
      name: editing?.name ?? '',
      slug: editing?.slug ?? '',
    });
  }, [open, editing, reset]);

  const error = create.error ?? update.error;
  const isPending = create.isPending || update.isPending;

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (editing) {
        await update.mutateAsync({ id: editing.id, input: data });
      } else {
        await create.mutateAsync(data);
      }
      onOpenChange(false);
    } catch {
      // surfaced via error
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'แก้ไขแท็ก' : 'สร้างแท็กใหม่'}</DialogTitle>
          <DialogDescription>
            แท็กเป็น flat labels (ไม่มีลำดับขั้น) ใช้เสริมหมวดในการจัดกลุ่มข่าว
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={(e) => void onSubmit(e)} className="space-y-3">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{taxonomyErrorMessage(error)}</AlertDescription>
            </Alert>
          )}

          <div>
            <Label htmlFor="tag-name" className="mb-1.5 block">
              ชื่อ *
            </Label>
            <Input
              id="tag-name"
              {...register('name', {
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  if (!slugManuallyEdited && !isEdit) {
                    setValue('slug', slugifyForPreview(e.target.value, 60));
                  }
                },
              })}
              placeholder="breaking-news"
            />
            {errors.name && <p className="text-destructive mt-1 text-xs">{errors.name.message}</p>}
          </div>

          <div>
            <Label htmlFor="tag-slug" className="mb-1.5 block">
              Slug
            </Label>
            <div className="flex gap-2">
              <Input
                id="tag-slug"
                {...register('slug', {
                  onChange: () => setSlugManuallyEdited(true),
                })}
                placeholder="breaking-news"
                className="font-mono"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => {
                  const name = watch('name') ?? '';
                  setValue('slug', slugifyForPreview(name, 60));
                  setSlugManuallyEdited(false);
                }}
                aria-label="สร้าง slug จากชื่อ"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
            {errors.slug && <p className="text-destructive mt-1 text-xs">{errors.slug.message}</p>}
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              ยกเลิก
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending
                ? isEdit
                  ? 'กำลังบันทึก...'
                  : 'กำลังสร้าง...'
                : isEdit
                  ? 'บันทึก'
                  : 'สร้าง'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
